/**
 * InitialLoginOnboardingModal — non-dismissible two-step wizard shown on the
 * user's first authenticated session.
 *
 * Step 1 (profile basics): first + last name (prefilled from signup, editable),
 * optional company, primary trade (searchable Radix combobox backed by
 * trade_categories), optional profile picture, and the CRM answer the signup
 * form no longer asks for.
 *
 * Step 2 (service areas): reuses StateGatedServiceAreaPicker in multi-select
 * mode. At least one area is required before saving.
 *
 * The final "Save and go to Dashboard" click uploads the avatar (if any) to
 * /api/files/avatar first — that endpoint writes `profile_image_url` itself,
 * so an upload failure is surfaced but never blocks the rest of the save —
 * then posts every other answer to /api/auth/initial-onboarding, which flips
 * `initial_onboarding_completed = true` atomically so the App.tsx gate
 * unmounts this component on the next refreshUser().
 *
 * Built on Radix Dialog primitives directly (not the shared DialogContent
 * wrapper) so the built-in Close X can be omitted and ESC / click-outside are
 * stubbed out — this is the "cannot be closed" contract the product requires.
 * The one escape hatch is the "Sign out" button on step 1: without it the
 * modal would be a trap for a user who does not want to answer right now.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowLeft, ArrowRight, Loader2, MapPin, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CrmSelect, EMPTY_CRM_SELECTION, type CrmSelectValue } from '@/components/auth/CrmSelect';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTradeSections, type Trade } from '@/hooks/useTrades';
import StateGatedServiceAreaPicker from '@/components/ui/StateGatedServiceAreaPicker';
import { normalizeCrmSelection } from '@/config/crm-options';
import type { GeoResult } from '@/lib/location-data';
import { submitInitialOnboarding } from '@/services/authService';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api, describeApiError } from '@/lib/api';
import { compressImage, isCompressibleImage } from '@/lib/imageUtils';

interface SelectedArea {
  city: string;
  state: string;
  /** Full label rendered on the confirmation chip. */
  label: string;
}

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

export function InitialLoginOnboardingModal() {
  const { user, refreshUser, logout } = useAuth();
  const { showError } = useToast();
  const navigate = useNavigate();

  // Two steps: 1 = profile basics, 2 = service areas.
  const [step, setStep] = useState<1 | 2>(1);

  // Trades grouped by section. Uses Radix Select (not the shared
  // TradeCategorySelect Popover) because Popover portals to document.body,
  // which Radix Dialog's react-remove-scroll blocks from scrolling — Select
  // is the primitive that plays correctly with a modal parent.
  const { data: tradeSections = [] } = useTradeSections();
  const tradesById = useMemo(() => {
    const map = new Map<string, Trade>();
    for (const section of tradeSections) {
      for (const trade of section.trades) map.set(trade.id, trade);
    }
    return map;
  }, [tradeSections]);

  // Step 1 state.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [primaryTrade, setPrimaryTrade] = useState<Trade | null>(null);
  const [crm, setCrm] = useState<CrmSelectValue>(EMPTY_CRM_SELECTION);
  const [crmError, setCrmError] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Step 2 state.
  const [serviceAreas, setServiceAreas] = useState<SelectedArea[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  // Prefill the name fields from the signup profile as soon as it lands.
  // Only seed once (when the local field is still blank) so the user's own
  // edits are never overwritten by a later refreshUser().
  useEffect(() => {
    if (!user) return;
    setFirstName((prev) => (prev ? prev : user.firstName ?? ''));
    setLastName((prev) => (prev ? prev : user.lastName ?? ''));
  }, [user]);

  // Free the object URL when the preview changes / component unmounts so we
  // don't leak blob handles between picks.
  useEffect(() => {
    if (!avatarPreview) return;
    return () => URL.revokeObjectURL(avatarPreview);
  }, [avatarPreview]);

  const canContinue = useMemo(() => {
    if (!firstName.trim() || !lastName.trim()) return false;
    if (!primaryTrade) return false;
    const crmResult = normalizeCrmSelection(crm.crmProvider, crm.crmProviderOther);
    return crmResult.ok;
  }, [firstName, lastName, primaryTrade, crm]);

  const canSave = serviceAreas.length > 0;

  const userInitials =
    user?.firstName && user?.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`
      : user?.email?.[0]?.toUpperCase() ?? 'U';

  const handleGeoResult = (result: GeoResult) => {
    const isCounty = result.type === 'county';
    const city = isCounty ? `${result.name} County` : result.name;
    const label = `${city}, ${result.stateCode}`;
    setServiceAreas((prev) => {
      // De-dupe case-insensitively — the backend does the same, but stopping
      // it here keeps the chip list clean.
      const key = label.toLowerCase();
      if (prev.some((a) => `${a.city}, ${a.state}`.toLowerCase() === key)) {
        return prev;
      }
      return [...prev, { city, state: result.stateCode, label }];
    });
  };

  const removeServiceArea = (index: number) => {
    setServiceAreas((prev) => prev.filter((_, i) => i !== index));
  };

  const openAvatarPicker = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so picking the same file twice still fires onChange.
    event.target.value = '';
    if (!file) return;

    let processed = file;
    if (file.size > MAX_AVATAR_BYTES) {
      if (isCompressibleImage(file)) {
        try {
          processed = await compressImage(file, 5, 0.85);
        } catch {
          showError('Compression failed', 'Could not compress that image. Please try a smaller file.');
          return;
        }
      } else {
        showError('File too large', 'Profile picture must be less than 5MB.');
        return;
      }
    }

    setAvatarFile(processed);
    setAvatarPreview(URL.createObjectURL(processed));
  };

  const clearAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview('');
  };

  const handleContinue = () => {
    const crmResult = normalizeCrmSelection(crm.crmProvider, crm.crmProviderOther);
    if (!crmResult.ok) {
      setCrmError(crmResult.message);
      return;
    }
    setCrmError('');
    if (!canContinue) return;
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave || !canContinue) return;

    setIsSaving(true);
    try {
      // Upload the avatar first, if the user picked one. The endpoint writes
      // profile_image_url itself, so we do NOT re-send it in the onboarding
      // payload. An upload failure is surfaced but must not block the rest of
      // the save — the picture is explicitly optional.
      if (avatarFile) {
        try {
          const form = new FormData();
          form.append('file', avatarFile);
          await api.post('/api/files/avatar', form);
        } catch (uploadError) {
          showError(
            'Profile picture not saved',
            describeApiError(uploadError) ||
              'We could not upload your profile picture. You can add it later from Settings.',
          );
        }
      }

      await submitInitialOnboarding({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        company: company.trim() ? company.trim() : undefined,
        primaryTradeId: primaryTrade!.id,
        crmProvider: crm.crmProvider,
        crmProviderOther:
          crm.crmProvider === 'other' ? crm.crmProviderOther.trim() : undefined,
        serviceAreas: serviceAreas.map((a) => ({ city: a.city, state: a.state })),
      });

      // Refresh the profile so the App.tsx gate sees the new flag and unmounts
      // this modal, then send the user to the dashboard.
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      showError('Could not save', describeApiError(error));
      setIsSaving(false);
    }
  };

  // Escape hatch: without this the modal is a trap — a user who does not want
  // to answer the wizard right now would land back on it every time they open
  // the app. Sign them out and send them to /login instead.
  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await logout();
    } catch {
      // logout() already swallows transport failures and cleared local state
      // first — nothing meaningful to surface here.
    }
    navigate('/login');
  };

  return (
    <DialogPrimitive.Root open>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg max-h-[calc(100vh-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <div className="mb-4">
            <DialogPrimitive.Title className="text-xl font-bold text-gray-900">
              {step === 1 ? 'Welcome — tell us about you' : 'Where do you work?'}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-gray-600 mt-1">
              {step === 1
                ? 'Step 1 of 2 — a few basics to set up your profile.'
                : 'Step 2 of 2 — pick every city or county you serve. Add as many as apply.'}
            </DialogPrimitive.Description>
            {/* Progress indicator — matches the "Step X of 2" wording above. */}
            <div className="mt-3 flex gap-2" aria-hidden="true">
              <span
                className={`h-1.5 flex-1 rounded-full ${
                  step >= 1 ? 'bg-indigo-500' : 'bg-gray-200'
                }`}
              />
              <span
                className={`h-1.5 flex-1 rounded-full ${
                  step >= 2 ? 'bg-indigo-500' : 'bg-gray-200'
                }`}
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {step === 1 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="initial-onboarding-first-name" className="text-sm font-semibold text-gray-700">
                      First name
                    </Label>
                    <Input
                      id="initial-onboarding-first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={isSaving}
                      autoComplete="given-name"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="initial-onboarding-last-name" className="text-sm font-semibold text-gray-700">
                      Last name
                    </Label>
                    <Input
                      id="initial-onboarding-last-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={isSaving}
                      autoComplete="family-name"
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="initial-onboarding-company" className="text-sm font-semibold text-gray-700">
                    Company name <span className="text-xs font-normal text-gray-500">(optional)</span>
                  </Label>
                  <Input
                    id="initial-onboarding-company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    disabled={isSaving}
                    autoComplete="organization"
                    placeholder="Acme HVAC"
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="initial-onboarding-primary-trade"
                    className="text-sm font-semibold text-gray-700"
                  >
                    Primary trade
                  </Label>
                  <Select
                    value={primaryTrade?.id ?? undefined}
                    onValueChange={(id) => setPrimaryTrade(tradesById.get(id) ?? null)}
                    disabled={isSaving || tradeSections.length === 0}
                  >
                    <SelectTrigger id="initial-onboarding-primary-trade" className="h-12">
                      <SelectValue placeholder="Select your primary trade" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {tradeSections.map((section) => (
                        <SelectGroup key={section.section}>
                          <SelectLabel>{section.section}</SelectLabel>
                          {section.trades.map((trade) => (
                            <SelectItem key={trade.id} value={trade.id}>
                              {trade.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">
                    Profile picture <span className="text-xs font-normal text-gray-500">(optional)</span>
                  </Label>
                  <div className="flex items-center gap-4">
                    <Avatar className="w-16 h-16 ring-4 ring-gray-100">
                      <AvatarImage src={avatarPreview || user?.profileImageUrl || undefined} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-lg font-bold">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={openAvatarPicker}
                          disabled={isSaving}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {avatarFile ? 'Change photo' : 'Upload photo'}
                        </Button>
                        {avatarFile && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={clearAvatar}
                            disabled={isSaving}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">JPG or PNG. Max 5MB.</p>
                    </div>
                  </div>
                </div>

                <CrmSelect
                  idPrefix="initial-onboarding"
                  value={crm}
                  onChange={(next) => {
                    setCrm(next);
                    setCrmError('');
                  }}
                  disabled={isSaving}
                  error={crmError}
                />

                <Button
                  type="button"
                  onClick={handleContinue}
                  disabled={!canContinue || isSaving || isSigningOut}
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="inline-flex items-center gap-2">
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSignOut}
                  disabled={isSaving || isSigningOut}
                  className="w-full h-11 text-gray-500 hover:text-gray-700 font-medium"
                >
                  {isSigningOut ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Signing out...
                    </span>
                  ) : (
                    'Sign out'
                  )}
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">
                    We use your service areas to match you with trade partners who work
                    the same regions. You can add more from your profile at any time.
                  </p>
                  <p className="text-xs text-gray-500">
                    Pick the state first, then search for the city or county you serve.
                  </p>
                  <StateGatedServiceAreaPicker
                    onSelect={handleGeoResult}
                    disabled={isSaving}
                    placeholder="Search city or county"
                    mapCenterHint={
                      serviceAreas[0]
                        ? `${serviceAreas[0].city}, ${serviceAreas[0].state}`
                        : undefined
                    }
                  />
                </div>

                {serviceAreas.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {serviceAreas.map((area, index) => (
                      <span
                        key={`${area.city}-${area.state}`}
                        className="inline-flex items-center gap-1 pl-3 pr-1 py-1 bg-indigo-100 text-indigo-800 text-sm font-medium rounded-full"
                      >
                        <MapPin className="w-3 h-3" />
                        {area.label}
                        <button
                          type="button"
                          onClick={() => removeServiceArea(index)}
                          disabled={isSaving}
                          aria-label={`Remove ${area.label}`}
                          className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-indigo-700 hover:bg-indigo-200 hover:text-indigo-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    disabled={isSaving}
                    className="sm:w-32 h-12 font-semibold"
                  >
                    <span className="inline-flex items-center gap-2">
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </span>
                  </Button>
                  <Button
                    type="submit"
                    disabled={!canSave || isSaving}
                    className="flex-1 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      'Save and go to Dashboard'
                    )}
                  </Button>
                </div>
              </>
            )}
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default InitialLoginOnboardingModal;
